// Column sorting for @lexical/table.
//
// Rows are real document nodes, so sorting reorders them for real - persisted and undoable. The row
// order from before the first sort is remembered per table so 'Remove sorting' can restore it. That
// memory is session-only (node keys are not stable across a reload), which matches the legacy table,
// where sorting itself vanished on reload.

import {
    $getTableColumnIndexFromTableCellNode,
    $getTableNodeFromLexicalNodeOrThrow,
    $isTableCellNode,
    $isTableRowNode,
    TableCellHeaderStates,
    TableCellNode,
    TableNode,
    TableRowNode,
} from "@lexical/table"
import {$getNodeByKey, LexicalEditor, NodeKey} from "lexical"

export type TableSortDirection = 'ascending' | 'descending'

type SortState = {
    columnIndex : number
    direction   : TableSortDirection
    rowKeys     : NodeKey[]
}

// ponytail: never pruned - bounded by the tables touched in one editor session
const sortStates = new Map<NodeKey, SortState>()

// The direction this cell's column is currently sorted by, if any
export const tableGetSortDirection = (cell: TableCellNode) => {
    const state = sortStates.get($getTableNodeFromLexicalNodeOrThrow(cell).getKey())
    if (!state || state.columnIndex !== $getTableColumnIndexFromTableCellNode(cell)) {
        return null
    }

    return state.direction
}

const getRows = (table: TableNode) => {
    return _.filter(table.getChildren(), $isTableRowNode) as TableRowNode[]
}

// Sorting is offered on top-row headers only, so row 0 is always pinned - as are any further leading
// rows whose first cell is a row header
const getBodyStart = (rows: TableRowNode[]) => {
    const headerCount = _.takeWhile(rows, row => {
        const first = row.getFirstChild()
        return $isTableCellNode(first) && first.hasHeaderState(TableCellHeaderStates.ROW)
    }).length

    return Math.max(1, headerCount)
}

// append() on an existing child moves it, so re-appending in order reorders the rows
const reorder = (table: TableNode, rows: TableRowNode[]) => {
    return _.forEach(rows, row => {
        table.append(row)
    })
}

// Empty cells sort last in both directions
const compareText = (a: string, b: string, direction: TableSortDirection) => {
    if (a === '' && b === '') {
        return 0
    }
    if (a === '') {
        return 1
    }
    if (b === '') {
        return -1
    }

    return direction === 'ascending' ? a.localeCompare(b) : b.localeCompare(a)
}

export const tableSortByColumn = (editor: LexicalEditor, cellKey: NodeKey, direction: TableSortDirection) => {
    editor.update(() => {
        const cell = $getNodeByKey(cellKey)
        if (!$isTableCellNode(cell)) {
            return
        }

        const table         = $getTableNodeFromLexicalNodeOrThrow(cell)
        const columnIndex   = $getTableColumnIndexFromTableCellNode(cell)
        const rows          = getRows(table)
        const body          = rows.slice(getBodyStart(rows))

        if (body.length < 2) {
            return
        }

        const previous = sortStates.get(table.getKey())

        sortStates.set(table.getKey(), {
            columnIndex,
            direction,
            rowKeys: previous ? previous.rowKeys : _.map(rows, row => row.getKey()),
        })

        const textAt = (row: TableRowNode) => {
            const cells = row.getChildren()
            return cells[columnIndex] ? cells[columnIndex].getTextContent().trim() : ''
        }

        const sorted = [...body].sort((a, b) => {
            return compareText(textAt(a), textAt(b), direction)
        })

        return reorder(table, sorted)
    })
}

export const tableRemoveSorting = (editor: LexicalEditor, cellKey: NodeKey) => {
    editor.update(() => {
        const cell = $getNodeByKey(cellKey)
        if (!$isTableCellNode(cell)) {
            return
        }

        const table = $getTableNodeFromLexicalNodeOrThrow(cell)
        const state = sortStates.get(table.getKey())
        if (!state) {
            return
        }

        sortStates.delete(table.getKey())

        // rows added since the sort have no remembered position - they keep the order they are in now
        const rows      = getRows(table)
        const restored  = _.filter(_.map(state.rowKeys, key => $getNodeByKey(key)), $isTableRowNode) as TableRowNode[]

        return reorder(table, [...restored, ..._.difference(rows, restored)])
    })
}
